import { formatDate } from "@/lib/utils";
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusCircle,
  Eye,
  List as ListIcon,
  Download,
  Trash2,
  ShoppingBag,
  Edit,
  Loader2,
  Mail,
  Printer,
  FileDown,
  RefreshCw,
  X,
  FileText,
  MoreVertical,
  DollarSign,
  CheckCircle,
  ShieldCheck,
  ShieldAlert,
  RotateCcw,
  CreditCard,
  Building,
  User,
  Clock,
  AlertCircle,
  Receipt,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getSaleItemReturnDetails, getSaleWarrantySummary } from "@/lib/warrantyUtils";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageContainer } from "@/components/layout/PageContainer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const SalesList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [showUnsent, setShowUnsent] = useState(false);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1); // reset to page 1 on search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText]);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currentStore = useSelector((state) => state.current_store);
  const storeId = currentStore?.id;
  const currencySymbol = currency?.symbol || "";

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Fetch payment methods available for sales scope
  const { data: availablePaymentMethods = [] } = useQuery({
    queryKey: ["payment-methods-available", "sales", storeId],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "sales",
          store_id: storeId || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: hasPermission("payment-method read"),
    staleTime: 5 * 60 * 1000,
  });

  const currentUser = useSelector((state) => state.user_data);

  const { data: salesData, isLoading: loadingSales } = useQuery({
    queryKey: [
      "sales",
      currentPage,
      pageSize,
      currentUser?.id,
      hasPermission("sale view_all"),
      debouncedSearchText,
      showUnsent,
      storeId,
    ],
    queryFn: async () => {
      const params = {
        page: currentPage,
        perPage: pageSize,
        sort_by: "created_at",
        sort_order: "desc",
      };

      if (storeId) {
        params.store_id = storeId;
      }

      if (debouncedSearchText) {
        params.search = debouncedSearchText;
      }

      if (!hasPermission("sale view_all") && hasPermission("sale view_own")) {
        params.user_id = currentUser?.id;
      }

      if (showUnsent) {
        params.unsent = 1;
      }

      const response = await apiClient.get(API_ENDPOINTS.SALES.SALES, {
        params,
      });
      return response.data;
    },
  });

  const sales = React.useMemo(() => salesData?.data || [], [salesData?.data]);
  const paginationMeta = {
    current_page: salesData?.meta?.current_page || salesData?.current_page || 1,
    last_page: salesData?.meta?.last_page || salesData?.last_page || 1,
    per_page: salesData?.meta?.per_page || salesData?.per_page || 10,
    total: salesData?.meta?.total || salesData?.total || sales.length,
  };

  const totalAmount = useMemo(() => {
    return sales.reduce(
      (sum, sale) => sum + parseFloat(sale.total_amount || 0),
      0,
    );
  }, [sales]);

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentSaleData, setCurrentSaleData] = useState(null);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewSaleData, setViewSaleData] = useState(null);

  const { data: rollbacksData, isLoading: loadingRollbacks } = useQuery({
    queryKey: ["saleRollbacks", viewSaleData?.id],
    queryFn: async () => {
      const response = await apiClient.get(
        API_ENDPOINTS.SALES.ROLLBACK(viewSaleData.id),
      );
      return response.data;
    },
    enabled:
      !!viewSaleData?.id &&
      viewModalOpen &&
      viewSaleData.status === "cancelled" &&
      viewSaleData.payment_status === "refunded",
  });
  const saleRollbacks = rollbacksData?.data || [];

  // Payments are already included in the sale record (viewSaleData.payments)
  const displayPayments = useMemo(() => {
    return Array.isArray(viewSaleData?.payments) ? viewSaleData.payments : [];
  }, [viewSaleData?.payments]);

  // Keep viewSaleData synced with latest salesData if refreshed
  useEffect(() => {
    if (viewSaleData?.id && salesData?.data) {
      const updated = salesData.data.find((s) => s.id === viewSaleData.id);
      if (updated && updated !== viewSaleData) {
        setViewSaleData(updated);
      }
    }
  }, [salesData, viewSaleData]);

  const activeCurrencySymbol =
    viewSaleData?.store?.default_currency?.symbol || currencySymbol || "$";

  const viewSaleTotals = useMemo(() => {
    if (!viewSaleData) {
      return {
        saleTotal: 0,
        totalPaid: 0,
        balanceDue: 0,
        isFullyPaid: false,
        isPartiallyPaid: false,
      };
    }
    const saleTotal = parseFloat(viewSaleData.total || 0);
    const payments = displayPayments || [];
    const completedPayments = payments.filter((p) =>
      ["completed", "paid", "partially-refunded"].includes(
        (p.payment_status || "").toLowerCase()
      )
    );
    let totalPaid = completedPayments.reduce(
      (sum, p) => sum + parseFloat(p.amount || 0),
      0
    );

    const isSalePaymentCompleted = ["completed", "paid"].includes(
      (viewSaleData.payment_status || "").toLowerCase()
    );

    // If payments list is empty but sale payment_status is marked completed/paid,
    // or if the sale is a zero-dollar completed transaction (e.g. warranty replacement or 100% discount)
    if (payments.length === 0 && isSalePaymentCompleted) {
      totalPaid = saleTotal;
    }

    const balanceDue = Math.max(0, saleTotal - totalPaid);
    const isZeroTotal = saleTotal <= 0.001;
    const isFullyPaid =
      isSalePaymentCompleted ||
      (isZeroTotal && balanceDue <= 0.001) ||
      (saleTotal > 0 && balanceDue <= 0.001);
    const isPartiallyPaid = !isFullyPaid && totalPaid > 0 && balanceDue > 0.001;

    return {
      saleTotal,
      totalPaid,
      balanceDue,
      isFullyPaid,
      isPartiallyPaid,
    };
  }, [viewSaleData, displayPayments]);

  const getPaymentMethodName = (payment) => {
    if (payment?.payment_method?.name) return payment.payment_method.name;
    if (payment?.payment_method?.code) {
      return payment.payment_method.code.replace(/[_-]/g, " ");
    }
    if (typeof payment?.payment_method === "string") {
      return payment.payment_method.replace(/[_-]/g, " ");
    }
    return "Payment";
  };

  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [saleItems, setSaleItems] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editItemData, setEditItemData] = useState({
    quantity: 1,
    unit_price: 0,
    discount_type: "fixed",
    discount_value: 0,
    discount_amount: 0,
    tax_amount: 0,
    notes: "",
  });

  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalMode, setPaymentModalMode] = useState("mark_as_paid"); // "mark_as_paid" | "record_payment"
  const [paymentSale, setPaymentSale] = useState(null);
  const [salePayments, setSalePayments] = useState([]);
  const [salePaymentsLoading, setSalePaymentsLoading] = useState(false);

  const emptyPaymentForm = () => ({
    amount: "",
    payment_method: "",
    payment_status: "completed",
    payment_date: new Date().toISOString().slice(0, 16),
    transaction_id: `TXN-${Date.now()}`,
    card_type: "",
    card_last_four: "",
    authorization_code: "",
    notes: "",
  });

  const [paymentFormData, setPaymentFormData] = useState(emptyPaymentForm());

  // Edit sale modal state
  const [editSaleModalOpen, setEditSaleModalOpen] = useState(false);
  const [editSaleData, setEditSaleData] = useState({
    id: null,
    status: "",
    payment_status: "",
    payment_method: "",
    sale_inv_date: "",
    notes: "",
  });

  // Rollback sale state
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollbackSale, setRollbackSale] = useState(null);
  const [rollbackItems, setRollbackItems] = useState([]); // Array of items to rollback
  const [rollbackFormData, setRollbackFormData] = useState({
    reason_code: "customer_refusal",
    reason_notes: "",
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(API_ENDPOINTS.SALES.SALE_DETAILS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["salePayments"] });
      toast.success("Sale deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete sale");
    },
  });

  const updateSaleMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await apiClient.put(API_ENDPOINTS.SALES.SALE_DETAILS(id), data);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Sale updated successfully");
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["salePayments"] });
      setEditSaleModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update sale");
    },
  });

  const rollbackSaleMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await apiClient.post(API_ENDPOINTS.SALES.ROLLBACK(id), data);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Rollback successful");
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["salePayments"] });
      setRollbackModalOpen(false);
      setRollbackSale(null);
      setRollbackItems([]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to process rollback",
      );
    },
  });

  const handleRollbackSale = (sale) => {
    setRollbackSale(sale);
    // Initialize rollbackItems with all sale items, setting rollback_quantity = 0 by default
    const initialRollbackItems = (sale.items || []).map((item) => ({
      ...item,
      rollback_quantity: 0,
      selected: false,
    }));
    setRollbackItems(initialRollbackItems);
    setRollbackFormData({
      reason_code: "customer_refusal",
      reason_notes: "",
    });
    setRollbackModalOpen(true);
  };

  const handleCloseRollbackModal = () => {
    setRollbackModalOpen(false);
    setRollbackSale(null);
    setRollbackItems([]);
  };

  const handleSubmitRollback = () => {
    // Filter selected items with quantity > 0
    const itemsToRollback = rollbackItems
      .filter((item) => item.selected && item.rollback_quantity > 0)
      .map((item) => ({
        sale_item_id: item.id,
        quantity: parseInt(item.rollback_quantity, 10),
      }));

    if (itemsToRollback.length === 0) {
      toast.error(
        "Please select at least one item and specify a valid quantity to rollback.",
      );
      return;
    }

    const payload = {
      reason_code: rollbackFormData.reason_code,
      reason_notes: rollbackFormData.reason_notes || null,
      idempotency_key: `rollback-${Date.now()}`,
      items: itemsToRollback,
    };

    rollbackSaleMutation.mutate({ id: rollbackSale.id, data: payload });
  };

  const handleEditSale = (sale) => {
    setEditSaleData({
      id: sale.id,
      status: sale.status || "",
      payment_status: sale.payment_status || "",
      payment_method: (typeof sale.payment_method === 'object' && sale.payment_method !== null) ? sale.payment_method.id : (sale.payment_method || ""),
      sale_inv_date: sale.sale_inv_date ? sale.sale_inv_date.slice(0, 16) : "",
      notes: sale.notes || "",
    });
    setEditSaleModalOpen(true);
  };

  const handleSubmitEditSale = () => {
    const payload = {};
    if (editSaleData.status) payload.status = editSaleData.status;
    if (editSaleData.payment_status)
      payload.payment_status = editSaleData.payment_status;
    if (editSaleData.payment_method)
      payload.payment_method = editSaleData.payment_method;
    if (editSaleData.sale_inv_date)
      payload.sale_inv_date = new Date(
        editSaleData.sale_inv_date,
      ).toISOString();
    payload.notes = editSaleData.notes || null;
    updateSaleMutation.mutate({ id: editSaleData.id, data: payload });
  };

  const pdfMutation = useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_INVOICE(id),
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Accept: "application/pdf, */*",
          },
          responseType: "blob",
          timeout: 30000,
          maxRedirects: 0,
          validateStatus: function (status) {
            return status >= 200 && status < 300;
          },
        },
      );

      if (response.status === 200) {
        const pdfBlob = new Blob([response.data], {
          type: "application/pdf",
        });

        if (pdfBlob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(blobUrl);
        return blobUrl;
      }
    },
    onError: (error) => {
      console.error("PDF Report error:", error);
      let errorMessage = "Failed to generate PDF report";

      if (error.code === "ECONNABORTED") {
        errorMessage = "Request timeout. Please try again.";
      } else if (error.response?.status === 204) {
        errorMessage = "No content available for PDF generation.";
      } else if (error.response?.status === 404) {
        errorMessage = "Invoice not found.";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }

      toast.error(errorMessage);
    },
  });

  const emailMutation = useMutation({
    mutationFn: async (data) => {
      const email = data?.customer?.email;
      if (!email || email === "" || email === "-" || !email.includes("@")) {
        throw new Error("Customer has no valid email address");
      }

      await apiClient.post(
        API_ENDPOINTS.SALES.SEND_INVOICE(data.id),
        null,
      );
    },
    onSuccess: () => {
      toast.success("Invoice email sent successfully!");
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["salePayments"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to send invoice email",
      );
    },
  });

  const generateReceiptMutation = useMutation({
    mutationFn: async (sale) => {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_RECEIPT(sale.id),
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200)
        throw new Error("Failed to generate Thermal Receipt");

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      return URL.createObjectURL(pdfBlob);
    },
    onSuccess: (blobUrl) => {
      const printWindow = window.open(blobUrl, "_blank");
      printWindow.onload = () => {
        printWindow.print();
      };
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to generate Thermal Receipt",
      );
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: async (updateData) => {
      const response = await apiClient.put(
        API_ENDPOINTS.SALES.SALE_ITEM(editingItem.id),
        updateData,
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Item updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["salePayments"] });
      if (selectedSale) {
        const updatedSales = queryClient.getQueryData(["sales"]);
        const currentSale = updatedSales?.find((s) => s.id === selectedSale.id);
        if (currentSale) {
          setSaleItems(currentSale.items || []);
        }
      }
      handleCloseEditItemModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update item");
    },
  });

  const createPaymentMutation = useMutation({
    mutationFn: async (paymentData) => {
      const response = await apiClient.post(
        API_ENDPOINTS.SALES.PAYMENTS,
        paymentData,
      );
      return response.data;
    },
    onSuccess: async () => {
      // Re-fetch all payments for this sale to get an accurate total
      // (the newly created payment is now included server-side)
      let saleBecameFullyPaid = false;
      const capturedSale = paymentSale; // capture before modal closes
      try {
        const paymentsRes = await apiClient.get(API_ENDPOINTS.SALES.PAYMENTS, {
          params: { sale_id: capturedSale.id, per_page: 100 },
        });
        const freshPayments = paymentsRes.data?.data || [];
        const totalPaid = calcTotalPaid(freshPayments);
        const saleTotal = parseFloat(capturedSale.total || 0);

        if (saleTotal > 0 && totalPaid >= saleTotal - 0.001) {
          // Fully covered - update sale payment_status to completed
          await apiClient.put(
            API_ENDPOINTS.SALES.SALE_DETAILS(capturedSale.id),
            { payment_status: "completed" },
          );
          saleBecameFullyPaid = true;
        }
      } catch {
        // Non-critical: payment was still recorded, just skip the status update
      }

      toast.success(
        saleBecameFullyPaid
          ? "Payment recorded - sale is now fully paid!"
          : paymentModalMode === "mark_as_paid"
            ? "Sale marked as paid successfully!"
            : "Payment recorded successfully!",
      );
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["salePayments"] });
      handleClosePaymentModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to record payment");
    },
  });

  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleGeneratePDFReport = async (id) => {
    pdfMutation.mutate(id);
  };

  const downloadPDF = () => {
    if (pdfBlobUrl && currentSaleData) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `invoice-${currentSaleData.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Invoice downloaded successfully!");
    }
  };

  const printPDF = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, "_blank");
      if (!printWindow) {
        alert("Please allow popups to print the invoice");
        return;
      }
      printWindow.onload = function () {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  };

  const handleClosePDFModal = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    pdfMutation.reset();
    setPdfModalOpen(false);
    setCurrentSaleData(null);
  };

  const handleViewSale = (sale) => {
    setViewModalOpen(true);
    setViewSaleData(sale);
    setCurrentSaleData(sale);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewSaleData(null);
  };

  const handleRecordPaymentFromView = (sale) => {
    handleCloseViewModal();
    handleRecordPayment(sale);
  };

  const handleMarkAsPaidFromView = (sale) => {
    handleCloseViewModal();
    handleMarkAsPaid(sale);
  };

  const parseProductData = (productData) => {
    if (!productData) return null;
    if (typeof productData === "object") return productData;
    try {
      return JSON.parse(productData);
    } catch {
      return null;
    }
  };

  const renderProductDetails = (item, product) => {
    if (!product) return <div className="font-medium">Unknown Product</div>;

    const returnDetails = getSaleItemReturnDetails(item);

    let serials = [];
    if (item.inventory_serials) {
      if (typeof item.inventory_serials === "object" && !Array.isArray(item.inventory_serials)) {
        if (item.inventory_serials.serial_number) serials = [item.inventory_serials.serial_number];
      } else if (Array.isArray(item.inventory_serials) && item.inventory_serials.length > 0) {
        serials = item.inventory_serials.map((s) => s?.serial_number).filter(Boolean);
      }
    } else if (item.inventory_serial?.serial_number) {
      serials = [item.inventory_serial.serial_number];
    } else if (Array.isArray(item.serial_numbers)) {
      serials = item.serial_numbers;
    } else if (
      typeof item.serial_numbers === "string" &&
      item.serial_numbers !== "null" &&
      item.serial_numbers.trim() !== ""
    ) {
      try {
        serials = JSON.parse(item.serial_numbers);
      } catch {
        serials = [item.serial_numbers];
      }
    } else if (item.serial_number) {
      serials = [item.serial_number];
    } else if (returnDetails.serialNumber) {
      serials = [returnDetails.serialNumber];
    }

    return (
      <div>
        <div className="font-medium flex items-center gap-2 flex-wrap">
          <span>{product.name || "Unknown Product"}</span>
          {returnDetails.isReturned && (
            <Badge
              variant="outline"
              className="text-[10px] h-4 px-1.5 py-0 border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold gap-1 inline-flex items-center"
              title={returnDetails.reason}
            >
              <RotateCcw className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
              <span>Returned ({returnDetails.label})</span>
            </Badge>
          )}
        </div>
        {(product.brand?.name || product.brand) && (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Brand: {typeof product.brand === "object" ? product.brand.name : product.brand}
          </div>
        )}
        {product.sku && (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            SKU: {product.sku}
          </div>
        )}
        {product.model && (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Model: {product.model}
          </div>
        )}
        {serials.length > 0 && (
          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            <span>Serial: {serials.join(", ")}</span>
            {returnDetails.isReturned ? (
              <Badge
                variant="outline"
                className="text-[9px] h-4 px-1 py-0 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold"
              >
                {returnDetails.label}
              </Badge>
            ) : item.inventory_serial?.status_info?.label ? (
              <Badge variant="outline" className="text-[9px] h-4 px-1 py-0">
                {item.inventory_serial.status_info.label}
              </Badge>
            ) : null}
          </div>
        )}
        {item.inventory_batch?.batch_number && (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Batch: {item.inventory_batch.batch_number}
          </div>
        )}
        {item.warranty_start_date && item.warranty_end_date && (
          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
            Warranty: {formatDate(item.warranty_start_date)}{" "}
            - {formatDate(item.warranty_end_date)}
            {item.warranty_status && !returnDetails.isReturned && (
              <Badge
                variant="outline"
                className="text-[9px] h-4 px-1 py-0 border-primary/20 text-primary"
              >
                {item.warranty_status.toUpperCase()}
              </Badge>
            )}
          </div>
        )}
        {item.warranty_notes && (
          <div className="text-[11px] text-muted-foreground mt-1">
            <span className="font-semibold">Warranty Notes:</span>{" "}
            <span dangerouslySetInnerHTML={{ __html: item.warranty_notes }} />
          </div>
        )}
        {item.notes && item.notes !== "none" && (
          <div className="text-[11px] text-muted-foreground mt-1">
            <span dangerouslySetInnerHTML={{ __html: item.notes }} />
          </div>
        )}
      </div>
    );
  };

  const handlePDFAction = async (sale) => {
    setCurrentSaleData(sale);
    setPdfModalOpen(true);
    handleGeneratePDFReport(sale.id);
  };

  const sendEmail = async (data) => {
    emailMutation.mutate(data);
  };

  const handleViewItems = async (sale) => {
    setSelectedSale(sale);
    setItemsModalOpen(true);
    setSaleItems(sale.items || []);
  };

  const handleCloseItemsModal = () => {
    setItemsModalOpen(false);
    setSaleItems([]);
    setSelectedSale(null);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditItemData({
      quantity: item.quantity,
      unit_price: parseFloat(item.unit_price),
      discount_type: item.discount_type || "fixed",
      discount_value: parseFloat(item.discount_value || 0),
      discount_amount: parseFloat(item.discount_amount || 0),
      tax_amount: parseFloat(item.tax_amount || 0),
      notes: item.notes || "",
    });
    setEditItemModalOpen(true);
  };

  const handleCloseEditItemModal = () => {
    setEditItemModalOpen(false);
    setEditingItem(null);
    setEditItemData({
      quantity: 1,
      unit_price: 0,
      discount_type: "fixed",
      discount_value: 0,
      discount_amount: 0,
      tax_amount: 0,
      notes: "",
    });
  };

  const handleEditItemChange = (e) => {
    const { name, value } = e.target;
    setEditItemData((prev) => {
      const newData = { ...prev, [name]: value };

      if (
        name === "quantity" ||
        name === "unit_price" ||
        name === "discount_type" ||
        name === "discount_value"
      ) {
        const quantity = parseInt(
          name === "quantity" ? value : newData.quantity,
        );
        const unitPrice = parseFloat(
          name === "unit_price" ? value : newData.unit_price,
        );
        const discountType =
          name === "discount_type" ? value : newData.discount_type;
        const discountValue = parseFloat(
          name === "discount_value" ? value : newData.discount_value,
        );

        let discountAmount = 0;
        if (discountType === "percentage") {
          discountAmount = (unitPrice * quantity * discountValue) / 100;
        } else {
          discountAmount = discountValue;
        }

        newData.discount_amount = discountAmount;
      }

      return newData;
    });
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;

    const quantity = parseInt(editItemData.quantity);
    const unitPrice = parseFloat(editItemData.unit_price);
    const discountAmount = parseFloat(editItemData.discount_amount);
    const taxAmount = parseFloat(editItemData.tax_amount);
    const lineTotal = quantity * unitPrice - discountAmount + taxAmount;

    const updateData = {
      quantity: quantity,
      unit_price: unitPrice,
      discount_type: editItemData.discount_type,
      discount_value: parseFloat(editItemData.discount_value),
      discount_amount: discountAmount,
      tax_amount: taxAmount,
      line_total: lineTotal,
      notes: editItemData.notes,
    };

    const saleItem = saleItems.find((item) => item.id === editingItem.id);
    const updatedSaleItem = { ...saleItem, ...updateData };

    updateItemMutation.mutate(updatedSaleItem);
  };

  // Sum only completed/partially-refunded payments as "money actually received"
  const calcTotalPaid = (payments) =>
    payments
      .filter((p) =>
        ["completed", "partially-refunded"].includes(p.payment_status),
      )
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  const fetchSalePayments = async (sale) => {
    setSalePaymentsLoading(true);
    try {
      const res = await apiClient.get(API_ENDPOINTS.SALES.PAYMENTS, {
        params: { sale_id: sale.id, per_page: 100 },
      });
      const payments = res.data?.data || [];
      setSalePayments(payments);

      const totalPaid = calcTotalPaid(payments);
      const remaining = Math.max(0, parseFloat(sale.total || 0) - totalPaid);

      setPaymentFormData({
        ...emptyPaymentForm(),
        payment_status: "completed",
        amount: remaining.toFixed(2),
      });
    } catch {
      setSalePayments([]);
      setPaymentFormData({
        ...emptyPaymentForm(),
        payment_status: "completed",
        amount: parseFloat(sale.total || 0).toFixed(2),
      });
    } finally {
      setSalePaymentsLoading(false);
    }
  };

  const handleMarkAsPaid = (sale) => {
    setPaymentSale(sale);
    setPaymentModalMode("mark_as_paid");
    setPaymentModalOpen(true);
    fetchSalePayments(sale, "mark_as_paid");
  };

  const handleRecordPayment = (sale) => {
    setPaymentSale(sale);
    setPaymentModalMode("record_payment");
    setPaymentModalOpen(true);
    fetchSalePayments(sale, "record_payment");
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentSale(null);
    setPaymentModalMode("mark_as_paid");
    setPaymentFormData(emptyPaymentForm());
    setSalePayments([]);
    createPaymentMutation.reset();
  };

  const handleSubmitPayment = () => {
    if (!paymentFormData.payment_method) {
      toast.error("Please select a payment method");
      return;
    }
    if (!paymentFormData.payment_date) {
      toast.error("Please select a payment date");
      return;
    }
    const amount = parseFloat(paymentFormData.amount);
    if (!paymentFormData.amount || isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (paymentModalMode === "mark_as_paid") {
      const totalPaid = calcTotalPaid(salePayments);
      const remaining = Math.max(
        0,
        parseFloat(paymentSale?.total || 0) - totalPaid,
      );
      if (Math.abs(amount - remaining) > 0.001) {
        toast.error(
          `Amount must equal the remaining balance (${currencySymbol}${remaining.toFixed(2)})`,
        );
        return;
      }
    }
    if (
      (availablePaymentMethods.find((m) => (m.id || m.slug || m) === paymentFormData.payment_method)?.name || paymentFormData.payment_method).toLowerCase().includes("card") &&
      paymentFormData.card_last_four &&
      paymentFormData.card_last_four.length !== 4
    ) {
      toast.error("Card last four digits must be exactly 4 characters");
      return;
    }

    const payload = {
      sale_id: paymentSale.id,
      amount: amount,
      payment_method: paymentFormData.payment_method,
      payment_status: "completed",
      payment_date: new Date(paymentFormData.payment_date).toISOString(),
      transaction_id: paymentFormData.transaction_id || undefined,
      notes: paymentFormData.notes || null,
    };

    // Card-specific fields - only include if card method is selected
    if ((availablePaymentMethods.find((m) => (m.id || m.slug || m) === paymentFormData.payment_method)?.name || paymentFormData.payment_method).toLowerCase().includes("card")) {
      if (paymentFormData.card_type)
        payload.card_type = paymentFormData.card_type;
      if (paymentFormData.card_last_four)
        payload.card_last_four = paymentFormData.card_last_four;
      if (paymentFormData.authorization_code)
        payload.authorization_code = paymentFormData.authorization_code;
    }

    createPaymentMutation.mutate(payload);
  };

  const getBadgeVariant = (status) => {
    const s = String(status || "").toLowerCase().replace(/_/g, "-");
    const variants = {
      completed: "success",
      paid: "success",
      active: "secondary",
      cancelled: "destructive",
      pending: "warning",
      unpaid: "destructive",
      failed: "destructive",
      refunded: "secondary",
      "on-hold": "warning",
      "partially-refunded": "warning",
      "partially-paid": "warning",
    };
    return variants[s] || "secondary";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "invoice_number",
        header: "Invoice No.",
        cell: ({ row }) => (
          <div className="space-y-1">
            <span className="font-medium">#{row.original.invoice_number}</span>
            <div>
              <Badge
                variant={getBadgeVariant(row.original.status)}
                className="capitalize"
              >
                {row.original.status.replace(/_/g, " ")}
              </Badge>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "customer",
        header: "Customer",
        cell: ({ row }) => {
          const customer = row.original.customer;
          return customer ? (
            <span className="text-sm">
              {customer.customer_type !== "individual" && customer.company
                ? customer.company
                : customer.full_name}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">
              Walk-in Customer
            </span>
          );
        },
      },
      {
        accessorKey: "store",
        header: "Store",
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {row.original.store ? row.original.store.name : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "item_count",
        header: "Items",
        cell: ({ row }) => {
          return (
            <Badge variant="outline" className="font-normal">
              {row.original.item_count}
            </Badge>
          );
        },
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ row }) => {
          return (
            <span className="font-semibold text-green-600">
              {currencySymbol}
              {parseFloat(row.original.total).toFixed(2)}
            </span>
          );
        },
      },
      {
        accessorKey: "payment_status",
        header: "Payment Status",
        cell: ({ row }) => {
          return (
            <Badge
              variant={getBadgeVariant(row.original.payment_status)}
              className="capitalize"
            >
              {row.original.payment_status.replace(/[_-]/g, " ")}
            </Badge>
          );
        },
      },
      {
        accessorKey: "user",
        header: "Created By",
        cell: ({ row }) => {
          const user = row.original.user;
          return (
            <span className="text-sm">
              {user ? user.employee?.full_name || user.username : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Date",
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatDate(row.original.created_at)}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hasPermission("sale read") && (
                <DropdownMenuItem onClick={() => handleViewSale(row.original)}>
                  <Eye className="mr-2 h-4 w-4" /> View Details
                </DropdownMenuItem>
              )}
              {hasPermission("sale item read") && (
                <DropdownMenuItem onClick={() => handleViewItems(row.original)}>
                  <ListIcon className="mr-2 h-4 w-4" /> View Items
                </DropdownMenuItem>
              )}
              {hasPermission("report sales invoice view") && (
                <DropdownMenuItem onClick={() => handlePDFAction(row.original)}>
                  <Download className="mr-2 h-4 w-4" /> Invoice PDF
                </DropdownMenuItem>
              )}
              {row.original.customer?.email &&
                row.original.customer?.email !== "" &&
                row.original.customer?.email !== "-" && (
                  <DropdownMenuItem onClick={() => sendEmail(row.original)}>
                    <Mail className="mr-2 h-4 w-4" /> Send Invoice
                  </DropdownMenuItem>
                )}
              <DropdownMenuSeparator />
              {hasPermission("sale update") && (
                <DropdownMenuItem onClick={() => handleEditSale(row.original)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit Sale
                </DropdownMenuItem>
              )}
              {row.original.status === "completed" &&
                hasPermission("sale rollback create") && (
                  <DropdownMenuItem
                    onClick={() => handleRollbackSale(row.original)}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" /> Rollback Sale
                  </DropdownMenuItem>
                )}
              {(row.original.payment_status === "pending" ||
                row.original.payment_status === "partially-paid" ||
                row.original.payment_status === "partially_paid") && (
                  <>
                    {hasPermission("payment create") && (
                      <DropdownMenuItem
                        onClick={() => handleMarkAsPaid(row.original)}
                        className="text-green-600 focus:text-green-600"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" /> Mark as Paid
                      </DropdownMenuItem>
                    )}
                    {hasPermission("payment create") && (
                      <DropdownMenuItem
                        onClick={() => handleRecordPayment(row.original)}
                      >
                        <DollarSign className="mr-2 h-4 w-4" /> Record Payment
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              {hasPermission("sale delete") && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => handleDelete(row.original.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol, hasPermission],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Sales List"
          subtitle="Manage your transactions"
        />
        <PageHeaderActions>
          {hasPermission("sale create") && (
            <Button onClick={() => navigate(all_routes.newSale)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              New Sale
            </Button>
          )}
          {hasPermission("sale create") && (
            <Button variant="outline" onClick={() => navigate(all_routes.pos)}>
              <ShoppingBag className="mr-2 h-4 w-4" />
              Point of Sale
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <div className="flex gap-4 items-center w-full">
          <FilterSearch
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search sales..."
          />
          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-sm font-medium">
            <input
              type="checkbox"
              checked={showUnsent}
              onChange={(e) => {
                setShowUnsent(e.target.checked);
                setCurrentPage(1);
              }}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            Show Unsent Invoices
          </label>
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={sales}
          onRowClick={handleViewSale}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          pageCount={paginationMeta.last_page}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          isLoading={loadingSales}
          enableRowSelection={true}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Sales: {paginationMeta.total}
              </span>
              {totalAmount > 0 && (
                <span className="font-medium text-foreground">
                  Total Amount: {currencySymbol}
                  {totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              )}
            </div>
          }
        />
      </TableCard>

      {/* PDF Modal */}
      <Dialog open={pdfModalOpen} onOpenChange={handleClosePDFModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice PDF
            </DialogTitle>
            <DialogDescription>
              Generate, download, print, or email the invoice for this sale.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {currentSaleData && (
              <div className="bg-muted p-4 rounded-lg">
                <h6 className="font-semibold mb-2">
                  Sale #{currentSaleData.invoice_number}
                </h6>
                <p className="text-muted-foreground text-sm">
                  <strong>Total:</strong> {currencySymbol}
                  {parseFloat(currentSaleData.total || 0).toFixed(2)}
                </p>
              </div>
            )}

            {pdfMutation.isPending && (
              <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 p-4 rounded-lg flex items-center border border-blue-200 dark:border-blue-800">
                <Loader2 className="mr-3 h-4 w-4 animate-spin" />
                <span>Generating PDF invoice...</span>
              </div>
            )}

            {pdfMutation.isSuccess && !pdfMutation.isPending && (
              <div>
                <div className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 p-4 rounded-lg flex items-center border border-green-200 dark:border-green-800 mb-4">
                  <FileText className="mr-2 h-4 w-4" />
                  PDF invoice generated successfully!
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button className="w-full" onClick={downloadPDF}>
                    <FileDown className="mr-2 h-4 w-4" />
                    Download PDF
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={printPDF}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    Print PDF
                  </Button>
                  {hasPermission("report sales receipt view") && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        generateReceiptMutation.mutate(currentSaleData)
                      }
                      disabled={generateReceiptMutation.isPending}
                      className="w-full gap-2 font-bold col-span-2"
                    >
                      {generateReceiptMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Printer className="h-4 w-4" />
                      )}
                      Print Thermal Receipt
                    </Button>
                  )}
                  {currentSaleData &&
                    currentSaleData.customer?.email &&
                    currentSaleData.customer.email !== "" &&
                    currentSaleData.customer.email !== "-" && (
                      <Button
                        className="w-full col-span-2"
                        variant="outline"
                        onClick={() => sendEmail(currentSaleData)}
                        disabled={emailMutation.isPending}
                      >
                        {emailMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending Invoice...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-4 w-4" />
                            Send Invoice Email
                          </>
                        )}
                      </Button>
                    )}
                </div>
              </div>
            )}

            {pdfMutation.isError &&
              !pdfMutation.isPending &&
              !pdfMutation.isSuccess && (
                <div className="bg-destructive/10 text-destructive p-4 rounded-lg border border-destructive/20">
                  <div className="flex items-center">
                    <X className="mr-2 h-4 w-4" />
                    PDF generation failed. Please try again.
                  </div>
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        pdfMutation.reset();
                        handleGeneratePDFReport(currentSaleData.id);
                      }}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Retry
                    </Button>
                  </div>
                </div>
              )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Sale Modal */}
      <Dialog open={viewModalOpen} onOpenChange={handleCloseViewModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                  <Eye className="h-5 w-5 text-primary" />
                  Sale Invoice #{viewSaleData?.invoice_number || "Loading..."}
                </DialogTitle>
                <DialogDescription className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                  <span>
                    Created:{" "}
                    {viewSaleData?.created_at
                      ? formatDate(viewSaleData.created_at)
                      : "-"}
                  </span>
                  {viewSaleData?.user && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3 text-muted-foreground" />
                        Billed by:{" "}
                        <strong className="font-medium text-foreground">
                          {viewSaleData.user.employee?.full_name ||
                            viewSaleData.user.username ||
                            "-"}
                        </strong>
                      </span>
                    </>
                  )}
                </DialogDescription>
              </div>
              {viewSaleData && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant={getBadgeVariant(viewSaleData.status)}
                    className="capitalize text-xs font-semibold px-2.5 py-1"
                  >
                    Sale: {viewSaleData.status.replace(/_/g, " ")}
                  </Badge>
                  <Badge
                    variant={getBadgeVariant(viewSaleData.payment_status)}
                    className="capitalize text-xs font-semibold px-2.5 py-1"
                  >
                    Payment:{" "}
                    {(viewSaleData.payment_status || "pending").replace(
                      /[_-]/g,
                      " ",
                    )}
                  </Badge>
                </div>
              )}
            </div>
          </DialogHeader>

          {viewSaleData && (
            <div className="space-y-6 px-5 py-4">
              {/* Top Financial KPI Metric Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="shadow-none border bg-muted/20">
                  <CardContent className="p-3.5">
                    <span className="text-xs text-muted-foreground block font-medium">
                      Total Amount
                    </span>
                    <span className="text-lg font-bold text-foreground mt-0.5 block">
                      {activeCurrencySymbol}
                      {viewSaleTotals.saleTotal.toFixed(2)}
                    </span>
                  </CardContent>
                </Card>
                <Card className="shadow-none border bg-emerald-500/5 border-emerald-500/20">
                  <CardContent className="p-3.5">
                    <span className="text-xs text-emerald-700 dark:text-emerald-400 block font-medium">
                      Total Paid
                    </span>
                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5 block">
                      {activeCurrencySymbol}
                      {viewSaleTotals.totalPaid.toFixed(2)}
                    </span>
                  </CardContent>
                </Card>
                <Card
                  className={`shadow-none border ${viewSaleTotals.balanceDue > 0.001
                    ? "bg-amber-500/5 border-amber-500/20"
                    : "bg-emerald-500/5 border-emerald-500/20"
                    }`}
                >
                  <CardContent className="p-3.5">
                    <span
                      className={`text-xs block font-medium ${viewSaleTotals.balanceDue > 0.001
                        ? "text-amber-700 dark:text-amber-400"
                        : "text-emerald-700 dark:text-emerald-400"
                        }`}
                    >
                      Balance Due
                    </span>
                    <span
                      className={`text-lg font-bold mt-0.5 block ${viewSaleTotals.balanceDue > 0.001
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-emerald-600 dark:text-emerald-400"
                        }`}
                    >
                      {activeCurrencySymbol}
                      {viewSaleTotals.balanceDue.toFixed(2)}
                    </span>
                  </CardContent>
                </Card>
                <Card className="shadow-none border bg-muted/20">
                  <CardContent className="p-3.5">
                    <span className="text-xs text-muted-foreground block font-medium">
                      Items Count
                    </span>
                    <span className="text-lg font-bold text-foreground mt-0.5 block">
                      {viewSaleData.item_count ||
                        viewSaleData.items?.length ||
                        0}{" "}
                      {(viewSaleData.item_count ||
                        viewSaleData.items?.length ||
                        0) === 1
                        ? "item"
                        : "items"}
                    </span>
                  </CardContent>
                </Card>
              </div>

              {/* Information Cards: Sale & Store Details + Customer Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sale Information Card */}
                <Card>
                  <CardHeader className="py-3 px-4 border-b">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-primary" />
                      Sale & Store Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2.5 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Invoice #:</span>
                      <span className="font-semibold text-foreground">
                        {viewSaleData.invoice_number}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Sale Date:</span>
                      <span className="font-medium">
                        {formatDate(viewSaleData.created_at)}
                      </span>
                    </div>
                    {viewSaleData.user && (
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Sales Rep:</span>
                        <span className="font-medium">
                          {viewSaleData.user.employee?.full_name ||
                            viewSaleData.user.username ||
                            "-"}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Email Sent:</span>
                      <span className="font-medium">
                        {viewSaleData.sent_at
                          ? formatDate(viewSaleData.sent_at)
                          : "No"}
                      </span>
                    </div>
                    {(() => {
                      const warrantySummary =
                        getSaleWarrantySummary(viewSaleData);
                      if (warrantySummary.hasReturned) {
                        return (
                          <div className="flex justify-between items-center pt-1 border-t">
                            <span className="text-muted-foreground">
                              Returns / Claims:
                            </span>
                            <Badge
                              variant="outline"
                              className="text-[10px] py-0 h-5 border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold gap-1"
                            >
                              <RotateCcw className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
                              {warrantySummary.allReturned
                                ? "All Items Returned"
                                : `${warrantySummary.returnedItems} of ${warrantySummary.totalItems} Returned`}
                            </Badge>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </CardContent>
                </Card>

                {/* Customer Details Card */}
                <Card>
                  <CardHeader className="py-3 px-4 border-b">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" />
                      Customer Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2.5 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Name:</span>
                      <span className="font-semibold text-foreground">
                        {viewSaleData.customer
                          ? viewSaleData.customer.customer_type !==
                            "individual" && viewSaleData.customer.company
                            ? viewSaleData.customer.company
                            : viewSaleData.customer.full_name
                          : "Walk-in Customer"}
                      </span>
                    </div>
                    {viewSaleData.customer && (
                      <>
                        {viewSaleData.customer.customer_type && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Type:</span>
                            <span className="capitalize font-medium">
                              {viewSaleData.customer.customer_type}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Email:</span>
                          <span className="font-medium">
                            {viewSaleData.customer.email || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Phone:</span>
                          <span className="font-medium">
                            {viewSaleData.customer.phone || "-"}
                          </span>
                        </div>
                        {(() => {
                          const address = [
                            viewSaleData.customer.house_number,
                            viewSaleData.customer.street_address,
                            viewSaleData.customer.city,
                            viewSaleData.customer.state,
                            viewSaleData.customer.postcode,
                            viewSaleData.customer.country,
                          ]
                            .filter(Boolean)
                            .join(", ");
                          if (!address) return null;
                          return (
                            <div className="flex justify-between items-start">
                              <span className="text-muted-foreground">
                                Address:
                              </span>
                              <span className="font-medium text-right max-w-[240px]">
                                {address}
                              </span>
                            </div>
                          );
                        })()}
                        {viewSaleData.customer.company_reg_no && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">
                              Company Reg #:
                            </span>
                            <span className="font-medium">
                              {viewSaleData.customer.company_reg_no}
                            </span>
                          </div>
                        )}
                        {viewSaleData.customer.tax_class && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">
                              Tax Class:
                            </span>
                            <span className="font-medium">
                              {viewSaleData.customer.tax_class}
                            </span>
                          </div>
                        )}
                        {viewSaleData.customer.contact_person && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">
                              Contact Person:
                            </span>
                            <span className="font-medium">
                              {viewSaleData.customer.contact_person}
                              {viewSaleData.customer.contact_person_phone &&
                                ` (${viewSaleData.customer.contact_person_phone})`}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Notes Callout if present */}
              {viewSaleData.notes && (
                <Card className="bg-amber-500/5 border-amber-500/20">
                  <CardContent className="p-3.5 text-xs flex items-start gap-2.5">
                    <FileText className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold text-amber-800 dark:text-amber-400 block mb-0.5">
                        Sale Notes:
                      </span>
                      <p className="text-muted-foreground whitespace-pre-wrap">
                        {viewSaleData.notes}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sale Items Table */}
              {viewSaleData.items && viewSaleData.items.length > 0 && (
                <Card>
                  <CardHeader className="py-3 px-4 border-b">
                    <CardTitle className="text-sm font-semibold flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <ShoppingBag className="h-4 w-4 text-primary" />
                        Sale Items ({viewSaleData.items.length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <ShadcnTable>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12 text-center">#</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Discount</TableHead>
                            <TableHead className="text-right">Tax</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="text-center w-20">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {viewSaleData.items.map((item, index) => {
                            const productData = parseProductData(
                              item.product_data,
                            );
                            const returnDetails =
                              getSaleItemReturnDetails(item);

                            return (
                              <TableRow key={item.id}>
                                <TableCell className="text-center font-medium text-xs">
                                  {index + 1}
                                </TableCell>
                                <TableCell>
                                  {renderProductDetails(
                                    item,
                                    productData || item.product,
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-xs">
                                  {item.quantity}
                                </TableCell>
                                <TableCell className="text-right text-xs">
                                  {parseFloat(item.original_price || 0) >
                                    parseFloat(item.unit_price || 0) ? (
                                    <div>
                                      <span className="line-through text-muted-foreground text-[10px] block">
                                        {activeCurrencySymbol}
                                        {parseFloat(
                                          item.original_price,
                                        ).toFixed(2)}
                                      </span>
                                      <span>
                                        {activeCurrencySymbol}
                                        {parseFloat(item.unit_price).toFixed(2)}
                                      </span>
                                    </div>
                                  ) : (
                                    <>
                                      {activeCurrencySymbol}
                                      {parseFloat(item.unit_price).toFixed(2)}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-xs text-destructive">
                                  {parseFloat(item.discount_amount || 0) > 0 ? (
                                    <>
                                      -{activeCurrencySymbol}
                                      {parseFloat(
                                        item.discount_amount,
                                      ).toFixed(2)}
                                    </>
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-xs text-muted-foreground">
                                  {parseFloat(item.tax_amount || 0) > 0 ? (
                                    <>
                                      {activeCurrencySymbol}
                                      {parseFloat(item.tax_amount).toFixed(2)}
                                    </>
                                  ) : item.tax_percentage &&
                                    parseFloat(item.tax_percentage) > 0 ? (
                                    `${parseFloat(item.tax_percentage)}%`
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-semibold text-xs text-foreground">
                                  {activeCurrencySymbol}
                                  {parseFloat(item.line_total).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-center">
                                  {hasPermission("warranty claim create") && (
                                    returnDetails.isReturned ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                disabled
                                                className="h-8 w-8 text-amber-600 opacity-60 cursor-not-allowed"
                                              >
                                                <ShieldAlert className="h-4 w-4" />
                                              </Button>
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>
                                              {returnDetails.reason ||
                                                "This item has already been returned / claimed under warranty."}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          navigate(all_routes.addWarrantyClaim, {
                                            state: {
                                              sale: viewSaleData,
                                              item: item,
                                            },
                                          });
                                        }}
                                        className="h-8 w-8 text-muted-foreground hover:text-primary"
                                        title="Create Warranty Claim"
                                      >
                                        <ShieldCheck className="h-4 w-4" />
                                      </Button>
                                    )
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </ShadcnTable>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Payments & Financial Summary in 2-Columns */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Payment Transactions Card (2 Columns Wide on large screens) */}
                <div className="lg:col-span-2">
                  <Card className="h-full flex flex-col">
                    <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-primary" />
                        Payment Transactions
                        {displayPayments.length > 0 && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] ml-1"
                          >
                            {displayPayments.length}
                          </Badge>
                        )}
                      </CardTitle>
                      {viewSaleTotals.balanceDue > 0.001 &&
                        (hasPermission("payment create") ||
                          hasPermission("pos payment create") ||
                          hasPermission("sale update")) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleRecordPaymentFromView(viewSaleData)
                            }
                            className="h-7 text-xs gap-1 border-primary/30 text-primary hover:bg-primary/5"
                          >
                            <CreditCard className="h-3 w-3" />
                            Record Payment
                          </Button>
                        )}
                    </CardHeader>
                    <CardContent className="p-0 flex-1 flex flex-col justify-between">
                      {displayPayments.length === 0 ? (
                        <div className="p-6 text-center text-muted-foreground space-y-2 my-auto">
                          <DollarSign className="h-8 w-8 mx-auto text-muted-foreground/30" />
                          <p className="text-xs font-medium">
                            {viewSaleTotals.saleTotal <= 0.001
                              ? "No payment required (Zero total sale / Settled)"
                              : "No payment records found for this sale"}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            Outstanding Balance:{" "}
                            <span
                              className={`font-semibold ${viewSaleTotals.balanceDue > 0.001
                                ? "text-destructive"
                                : "text-emerald-600"
                                }`}
                            >
                              {activeCurrencySymbol}
                              {viewSaleTotals.balanceDue.toFixed(2)}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <ShadcnTable>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10 text-center">#</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Method</TableHead>
                                <TableHead>Transaction ID</TableHead>
                                <TableHead className="text-center">Status</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {displayPayments.map((payment, idx) => {
                                const methodName = getPaymentMethodName(payment);
                                const pStatus =
                                  payment.payment_status || "completed";

                                return (
                                  <TableRow key={payment.id || idx}>
                                    <TableCell className="text-center text-xs font-medium">
                                      {idx + 1}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                      {payment.formatted_payment_date ||
                                        (payment.payment_date
                                          ? formatDate(payment.payment_date)
                                          : "-")}
                                    </TableCell>
                                    <TableCell className="text-xs font-medium">
                                      <Badge
                                        variant="outline"
                                        className="text-[11px] capitalize font-normal bg-muted/40"
                                      >
                                        {methodName}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs">
                                      <span className="font-mono bg-muted/50 px-1.5 py-0.5 rounded border text-[11px] select-all">
                                        {payment.transaction_id || "-"}
                                      </span>
                                      {payment.authorization_code && (
                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                          Auth: {payment.authorization_code}
                                        </div>
                                      )}
                                      {payment.notes && (
                                        <div className="text-[10px] text-muted-foreground italic mt-0.5">
                                          "{payment.notes}"
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Badge
                                        variant={getBadgeVariant(pStatus)}
                                        className="text-[10px] capitalize font-semibold"
                                      >
                                        {pStatus.replace(/[_-]/g, " ")}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-semibold text-xs text-foreground">
                                      {payment.formatted_amount
                                        ? `${activeCurrencySymbol}${payment.formatted_amount}`
                                        : `${activeCurrencySymbol}${parseFloat(
                                          payment.amount || 0,
                                        ).toFixed(2)}`}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </ShadcnTable>
                        </div>
                      )}

                      {/* Payment Status Summary Bar */}
                      <div className="p-3 bg-muted/30 border-t flex flex-wrap items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Status:</span>
                          {viewSaleTotals.isFullyPaid ? (
                            <Badge variant="success" className="text-[10px] gap-1">
                              <CheckCircle className="h-3 w-3" /> Fully Paid
                            </Badge>
                          ) : viewSaleTotals.isPartiallyPaid ? (
                            <Badge variant="warning" className="text-[10px] gap-1">
                              <Clock className="h-3 w-3" /> Partially Paid
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="text-[10px] gap-1">
                              <AlertCircle className="h-3 w-3" /> Unpaid / Pending
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-muted-foreground mr-1.5">
                              Paid:
                            </span>
                            <span className="font-bold text-emerald-600">
                              {activeCurrencySymbol}
                              {viewSaleTotals.totalPaid.toFixed(2)}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground mr-1.5">
                              Due:
                            </span>
                            <span
                              className={`font-bold ${viewSaleTotals.balanceDue > 0.001
                                ? "text-destructive"
                                : "text-emerald-600"
                                }`}
                            >
                              {activeCurrencySymbol}
                              {viewSaleTotals.balanceDue.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Financial Summary Card (1 Column Wide) */}
                <div>
                  <Card className="h-full flex flex-col justify-between">
                    <CardHeader className="py-3 px-4 border-b">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-primary" />
                        Financial Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-2.5 text-xs flex-1">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span className="font-medium text-foreground">
                          {activeCurrencySymbol}
                          {parseFloat(viewSaleData.subtotal).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Discount:</span>
                        <span className="font-medium text-destructive">
                          {parseFloat(viewSaleData.discount_amount || 0) > 0 ? (
                            <>
                              -{activeCurrencySymbol}
                              {parseFloat(
                                viewSaleData.discount_amount,
                              ).toFixed(2)}
                              {viewSaleData.discount_type &&
                                viewSaleData.discount_type !== "none" && (
                                  <span className="text-[10px] text-muted-foreground ml-1 font-normal capitalize">
                                    ({viewSaleData.discount_type}
                                    {viewSaleData.discount_value &&
                                      viewSaleData.discount_type === "percentage"
                                      ? ` ${parseFloat(viewSaleData.discount_value)}%`
                                      : ""}
                                    )
                                  </span>
                                )}
                            </>
                          ) : (
                            `${activeCurrencySymbol}0.00`
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">
                          Tax{" "}
                          {viewSaleData.store?.default_tax_class
                            ? `(${viewSaleData.store.default_tax_class})`
                            : ""}
                          :
                        </span>
                        <span className="font-medium text-foreground">
                          {activeCurrencySymbol}
                          {parseFloat(viewSaleData.tax_amount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="pt-2 border-t flex justify-between items-center">
                        <span className="font-bold text-sm">Total Sale:</span>
                        <span className="font-bold text-base text-foreground">
                          {activeCurrencySymbol}
                          {viewSaleTotals.saleTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="pt-2 border-t space-y-1.5">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Total Paid:
                          </span>
                          <span className="font-bold text-emerald-600">
                            {activeCurrencySymbol}
                            {viewSaleTotals.totalPaid.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">
                            Balance Due:
                          </span>
                          <span
                            className={`font-bold text-sm ${viewSaleTotals.balanceDue > 0.001
                              ? "text-destructive"
                              : "text-emerald-600"
                              }`}
                          >
                            {activeCurrencySymbol}
                            {viewSaleTotals.balanceDue.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Rollback History */}
              {loadingRollbacks ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                saleRollbacks.length > 0 &&
                hasPermission("sale rollback read") && (
                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-semibold flex items-center gap-2 text-destructive text-sm">
                      <RefreshCw className="h-4 w-4" /> Rollback History
                    </h4>
                    {saleRollbacks.map((rollback) => (
                      <Card
                        key={rollback.id}
                        className="bg-destructive/5 border-destructive/20 shadow-none"
                      >
                        <CardHeader className="py-3 px-4">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              {rollback.is_full_rollback
                                ? "Full Rollback"
                                : "Partial Rollback"}
                              <Badge
                                variant="outline"
                                className="text-xs bg-background"
                              >
                                {rollback.reason_code.replace(/_/g, " ")}
                              </Badge>
                            </CardTitle>
                            <span className="text-xs text-muted-foreground">
                              {new Date(rollback.created_at).toLocaleString()}
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="px-4 pb-4 space-y-3">
                          {rollback.reason_notes && (
                            <div className="text-sm text-muted-foreground bg-background/50 p-2 rounded border">
                              <strong>Notes:</strong> {rollback.reason_notes}
                            </div>
                          )}
                          {rollback.credit_note && (
                            <div className="text-sm flex items-center justify-between bg-background border p-2 rounded-md">
                              <div>
                                <span className="font-medium">
                                  Credit Note:
                                </span>{" "}
                                <span className="text-muted-foreground">
                                  {rollback.credit_note.credit_note_number}
                                </span>
                              </div>
                              <span className="font-semibold text-destructive">
                                {activeCurrencySymbol}
                                {parseFloat(rollback.credit_note.total).toFixed(
                                  2,
                                )}
                              </span>
                            </div>
                          )}
                          <div className="rounded-md border bg-background overflow-x-auto">
                            <ShadcnTable>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="py-2 h-auto text-xs">
                                    Product
                                  </TableHead>
                                  <TableHead className="text-right py-2 h-auto text-xs">
                                    Qty
                                  </TableHead>
                                  <TableHead className="text-right py-2 h-auto text-xs">
                                    Unit Price
                                  </TableHead>
                                  <TableHead className="text-right py-2 h-auto text-xs">
                                    Line Total
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {rollback.items.map((item) => (
                                  <TableRow key={item.id}>
                                    <TableCell className="py-2 text-xs">
                                      {renderProductDetails(item, item.product)}
                                    </TableCell>
                                    <TableCell className="text-right py-2 text-xs">
                                      {item.quantity}
                                    </TableCell>
                                    <TableCell className="text-right py-2 text-xs">
                                      {activeCurrencySymbol}
                                      {parseFloat(item.unit_price).toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-right py-2 text-xs font-semibold">
                                      {activeCurrencySymbol}
                                      {parseFloat(item.line_total).toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </ShadcnTable>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {viewSaleData && (
            <DialogFooter className="px-5 py-3 border-t sm:justify-between items-center gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {hasPermission("report sales receipt view") && (
                  <Button
                    variant="outline"
                    onClick={() => generateReceiptMutation.mutate(viewSaleData)}
                    disabled={generateReceiptMutation.isPending}
                    className="gap-1.5 text-xs font-semibold"
                    size="sm"
                  >
                    {generateReceiptMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Printer className="h-3.5 w-3.5" />
                    )}
                    Print Thermal Receipt
                  </Button>
                )}
                {hasPermission("report sales invoice view") && (
                  <Button
                    variant="outline"
                    onClick={() => handlePDFAction(viewSaleData)}
                    disabled={pdfMutation.isPending}
                    className="gap-1.5 text-xs font-semibold"
                    size="sm"
                  >
                    {pdfMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Invoice PDF
                  </Button>
                )}
                {viewSaleData.customer?.email &&
                  viewSaleData.customer.email !== "" &&
                  viewSaleData.customer.email !== "-" && (
                    <Button
                      variant="outline"
                      onClick={() => sendEmail(viewSaleData)}
                      disabled={emailMutation.isPending}
                      className="gap-1.5 text-xs font-semibold"
                      size="sm"
                    >
                      {emailMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Mail className="h-3.5 w-3.5" />
                      )}
                      Send Email
                    </Button>
                  )}
                {viewSaleTotals.balanceDue > 0.001 &&
                  (hasPermission("payment create") ||
                    hasPermission("pos payment create") ||
                    hasPermission("sale update")) && (
                    <Button
                      onClick={() => handleMarkAsPaidFromView(viewSaleData)}
                      className="gap-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      Mark as Paid
                    </Button>
                  )}
              </div>
              <Button variant="outline" size="sm" onClick={handleCloseViewModal}>
                Close
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Items List Modal */}
      <Dialog open={itemsModalOpen} onOpenChange={handleCloseItemsModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <ListIcon className="h-5 w-5" />
              Sale Items - {selectedSale?.invoice_number}
            </DialogTitle>
            <DialogDescription>
              View and edit the individual line items for this sale.
            </DialogDescription>
          </DialogHeader>

          {selectedSale && (
            <div className="space-y-4 px-5 pb-5">
              <div className="rounded-md border overflow-x-auto">
                <ShadcnTable>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {saleItems.map((item, index) => {
                      const productData = parseProductData(item.product_data);
                      return (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            {renderProductDetails(
                              item,
                              productData || item.product,
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.quantity}
                          </TableCell>
                          <TableCell className="text-right">
                            {currencySymbol}
                            {parseFloat(item.unit_price).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right text-destructive">
                            {currencySymbol}
                            {parseFloat(item.discount_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            {currencySymbol}
                            {parseFloat(item.tax_amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {currencySymbol}
                            {parseFloat(item.line_total).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center">
                            {hasPermission("sale item update") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditItem(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </ShadcnTable>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Item Modal */}
      <Dialog open={editItemModalOpen} onOpenChange={handleCloseEditItemModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Item
            </DialogTitle>
            <DialogDescription>
              Update the quantity, price, discount, or tax for this line item.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  name="quantity"
                  value={editItemData.quantity}
                  onChange={handleEditItemChange}
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label>Unit Price</Label>
                <Input
                  type="number"
                  name="unit_price"
                  value={editItemData.unit_price}
                  onChange={handleEditItemChange}
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select
                  value={editItemData.discount_type}
                  onValueChange={(value) =>
                    handleEditItemChange({
                      target: { name: "discount_type", value },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Discount Value</Label>
                <Input
                  type="number"
                  name="discount_value"
                  value={editItemData.discount_value}
                  onChange={handleEditItemChange}
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label>Tax Amount</Label>
                <Input
                  type="number"
                  name="tax_amount"
                  value={editItemData.tax_amount}
                  onChange={handleEditItemChange}
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label>Discount Amount</Label>
                <Input
                  type="number"
                  value={editItemData.discount_amount}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                name="notes"
                value={editItemData.notes}
                onChange={handleEditItemChange}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditItemModal}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateItem}
              disabled={updateItemMutation.isPending}
            >
              {updateItemMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal - Mark as Paid / Record Payment */}
      <Dialog open={paymentModalOpen} onOpenChange={handleClosePaymentModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentModalMode === "mark_as_paid" ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Mark as Paid
                </>
              ) : (
                <>
                  <DollarSign className="h-5 w-5" />
                  Record Payment
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {paymentSale
                ? `Invoice #${paymentSale.invoice_number} - ${paymentModalMode === "mark_as_paid" ? "Record full remaining balance to mark this sale as paid." : "Record a partial or full payment against this sale."}`
                : paymentModalMode === "mark_as_paid"
                  ? "Record a full payment to mark this sale as paid."
                  : "Record a partial or full payment against this sale."}
            </DialogDescription>
          </DialogHeader>

          {/* Balance summary bar */}
          {paymentSale && (
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-muted/50 border shadow-none">
                <CardContent className="p-3 text-center">
                  <p className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
                    Sale Total
                  </p>
                  <p className="text-lg font-bold tracking-tight">
                    {currencySymbol}
                    {parseFloat(paymentSale.total || 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-muted/50 border shadow-none">
                <CardContent className="p-3 text-center">
                  <p className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
                    Already Paid
                  </p>
                  {salePaymentsLoading ? (
                    <div className="flex justify-center items-center h-7">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <p className="text-lg font-bold tracking-tight text-emerald-600">
                      {currencySymbol}
                      {calcTotalPaid(salePayments).toFixed(2)}
                    </p>
                  )}
                </CardContent>
              </Card>
              <Card
                className={
                  !salePaymentsLoading &&
                    parseFloat(paymentSale.total || 0) -
                    calcTotalPaid(salePayments) <=
                    0.001
                    ? "bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800 shadow-none"
                    : "bg-slate-900 dark:bg-slate-800 border-slate-700 shadow-none"
                }
              >
                <CardContent className="p-3 text-center">
                  <p
                    className={
                      !salePaymentsLoading &&
                        parseFloat(paymentSale.total || 0) -
                        calcTotalPaid(salePayments) <=
                        0.001
                        ? "text-[10px] uppercase font-semibold mb-1 text-emerald-600 dark:text-emerald-400"
                        : "text-[10px] uppercase font-semibold mb-1 text-slate-400"
                    }
                  >
                    {!salePaymentsLoading &&
                      parseFloat(paymentSale.total || 0) -
                      calcTotalPaid(salePayments) <=
                      0.001
                      ? "Fully Paid"
                      : "Remaining"}
                  </p>
                  {salePaymentsLoading ? (
                    <div className="flex justify-center items-center h-7">
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <p
                      className={
                        parseFloat(paymentSale.total || 0) -
                          calcTotalPaid(salePayments) <=
                          0.001
                          ? "text-lg font-bold tracking-tight text-emerald-700 dark:text-emerald-400"
                          : "text-lg font-bold tracking-tight text-white"
                      }
                    >
                      {currencySymbol}
                      {Math.max(
                        0,
                        parseFloat(paymentSale.total || 0) -
                        calcTotalPaid(salePayments),
                      ).toFixed(2)}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Main layout: form + history */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
            {/* Left: payment form */}
            <div className="md:col-span-3 space-y-4">
              {/* Amount */}
              <div className="space-y-2">
                <Label>
                  Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={paymentFormData.amount}
                  onChange={(e) =>
                    setPaymentFormData({
                      ...paymentFormData,
                      amount: e.target.value,
                    })
                  }
                  disabled={paymentModalMode === "mark_as_paid"}
                  className={
                    paymentModalMode === "mark_as_paid" ? "bg-muted" : ""
                  }
                />
                {paymentModalMode === "mark_as_paid" && (
                  <p className="text-xs text-muted-foreground">
                    Locked to the remaining unpaid balance.
                  </p>
                )}
              </div>

              {/* Payment Method & Transaction ID */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>
                    Method <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={paymentFormData.payment_method}
                    onValueChange={(value) =>
                      setPaymentFormData({
                        ...paymentFormData,
                        payment_method: value,
                        card_type: "",
                        card_last_four: "",
                        authorization_code: "",
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePaymentMethods.map((pm) => (
                        <SelectItem key={pm.id || pm.slug || pm} value={pm.id || pm.slug || pm}>
                          {pm.name || pm}
                        </SelectItem>
                      ))}






                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Transaction ID</Label>
                  <Input
                    placeholder="TXN-..."
                    value={paymentFormData.transaction_id}
                    onChange={(e) =>
                      setPaymentFormData({
                        ...paymentFormData,
                        transaction_id: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              {/* Payment Date */}
              <div className="space-y-2">
                <Label>
                  Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="datetime-local"
                  value={paymentFormData.payment_date}
                  onChange={(e) =>
                    setPaymentFormData({
                      ...paymentFormData,
                      payment_date: e.target.value,
                    })
                  }
                />
              </div>

              {/* Card details - only when method is card */}
              {(availablePaymentMethods.find((m) => (m.id || m.slug || m) === paymentFormData.payment_method)?.name || paymentFormData.payment_method).toLowerCase().includes("card") && (
                <div className="grid grid-cols-2 gap-3 rounded-md border border-dashed p-3">
                  <div className="space-y-1 col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wide">
                      Card Details
                    </Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Card Type</Label>
                    <Select
                      value={paymentFormData.card_type}
                      onValueChange={(value) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          card_type: value,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="visa">Visa</SelectItem>
                        <SelectItem value="mastercard">Mastercard</SelectItem>
                        <SelectItem value="amex">Amex</SelectItem>
                        <SelectItem value="discover">Discover</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Last 4 Digits</Label>
                    <Input
                      placeholder="1234"
                      maxLength={4}
                      value={paymentFormData.card_last_four}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          card_last_four: e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 4),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Authorization Code</Label>
                    <Input
                      placeholder="Auth code from card terminal"
                      value={paymentFormData.authorization_code}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          authorization_code: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Optional payment notes..."
                  rows={2}
                  value={paymentFormData.notes}
                  onChange={(e) =>
                    setPaymentFormData({
                      ...paymentFormData,
                      notes: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            {/* Right: payment history */}
            <div className="md:col-span-2 flex flex-col gap-3">
              <p className="text-xs uppercase font-semibold text-muted-foreground">
                Payment History
              </p>

              <ScrollArea className="flex-1 min-h-[180px] max-h-[280px] rounded-md border bg-muted/30 p-2">
                {salePaymentsLoading ? (
                  <div className="h-40 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : salePayments.length === 0 ? (
                  <div className="h-40 flex flex-col items-center justify-center text-muted-foreground gap-2">
                    <DollarSign className="h-8 w-8 opacity-20" />
                    <p className="text-xs font-medium">No payments yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {salePayments.map((p, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-md bg-background px-3 py-2 shadow-sm border"
                      >
                        <div className="min-w-0 space-y-0.5">
                          <Badge
                            variant="secondary"
                            className="capitalize text-[10px] font-semibold"
                          >
                            {typeof p.payment_method === 'object' && p.payment_method !== null ? p.payment_method.name : (p.payment_method || "").replace("_", " ")}
                          </Badge>
                          <p className="text-[10px] text-muted-foreground truncate max-w-[110px]">
                            {p.transaction_id || "No reference"}
                          </p>
                          {p.formatted_payment_date && (
                            <p className="text-[10px] text-muted-foreground">
                              {p.formatted_payment_date}
                            </p>
                          )}
                        </div>
                        <div className="ml-2 text-right">
                          <span className="font-bold text-emerald-600 whitespace-nowrap text-sm block">
                            {currencySymbol}
                            {parseFloat(p.amount || 0).toFixed(2)}
                          </span>
                          <Badge
                            variant="outline"
                            className="text-[9px] capitalize mt-0.5"
                          >
                            {p.payment_status || "-"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              <div className="space-y-1.5 pt-2 border-t">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Total Paid</span>
                  <span className="font-semibold text-emerald-600">
                    {currencySymbol}
                    {calcTotalPaid(salePayments).toFixed(2)}
                  </span>
                </div>
                {paymentSale && (
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm">Remaining</span>
                    <span
                      className={
                        parseFloat(paymentSale.total || 0) -
                          calcTotalPaid(salePayments) <=
                          0.001
                          ? "text-base font-bold text-emerald-600"
                          : "text-base font-bold text-destructive"
                      }
                    >
                      {currencySymbol}
                      {Math.max(
                        0,
                        parseFloat(paymentSale.total || 0) -
                        calcTotalPaid(salePayments),
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClosePaymentModal}
              disabled={createPaymentMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPayment}
              disabled={createPaymentMutation.isPending || salePaymentsLoading}
              className={
                paymentModalMode === "mark_as_paid"
                  ? "bg-green-600 hover:bg-green-700"
                  : ""
              }
            >
              {createPaymentMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {paymentModalMode === "mark_as_paid"
                ? "Mark as Paid"
                : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Modal */}
      <Dialog open={editSaleModalOpen} onOpenChange={setEditSaleModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Sale
            </DialogTitle>
            <DialogDescription>
              Update sale status, payment information, invoice date, or notes.
              Financial totals and line items are managed separately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Status row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sale Status</Label>
                <Select
                  value={editSaleData.status}
                  onValueChange={(v) =>
                    setEditSaleData({ ...editSaleData, status: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Payment Status</Label>
                <Select
                  value={editSaleData.payment_status}
                  onValueChange={(v) =>
                    setEditSaleData({ ...editSaleData, payment_status: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="on-hold">On Hold</SelectItem>
                    <SelectItem value="partially-refunded">
                      Partially Refunded
                    </SelectItem>
                    <SelectItem value="partially-paid">
                      Partially Paid
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Internal notes for this sale..."
                rows={3}
                value={editSaleData.notes}
                onChange={(e) =>
                  setEditSaleData({ ...editSaleData, notes: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditSaleModalOpen(false)}
              disabled={updateSaleMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitEditSale}
              disabled={updateSaleMutation.isPending}
            >
              {updateSaleMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Modal */}
      <Dialog open={rollbackModalOpen} onOpenChange={handleCloseRollbackModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Rollback Sale - {rollbackSale?.invoice_number}
            </DialogTitle>
            <DialogDescription>
              Select the items and quantities you want to rollback, and provide
              a reason.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 px-5 pb-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reason Code</Label>
                <Select
                  value={rollbackFormData.reason_code}
                  onValueChange={(value) =>
                    setRollbackFormData((prev) => ({
                      ...prev,
                      reason_code: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer_refusal">
                      Customer Refusal
                    </SelectItem>
                    <SelectItem value="cashier_error">Cashier Error</SelectItem>
                    <SelectItem value="duplicate_transaction">
                      Duplicate Transaction
                    </SelectItem>
                    <SelectItem value="product_defect">
                      Product Defect
                    </SelectItem>
                    <SelectItem value="pricing_error">Pricing Error</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reason Notes (Optional)</Label>
                <Textarea
                  rows={2}
                  maxLength={500}
                  value={rollbackFormData.reason_notes}
                  onChange={(e) =>
                    setRollbackFormData((prev) => ({
                      ...prev,
                      reason_notes: e.target.value,
                    }))
                  }
                  placeholder="Provide additional details..."
                />
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Select Items to Rollback
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-x-auto">
                  <ShadcnTable>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Select</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Sold Qty</TableHead>
                        <TableHead className="text-right">
                          Rollback Qty
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rollbackItems.map((item) => {
                        const productData = parseProductData(item.product_data);
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setRollbackItems((prev) =>
                                    prev.map((i) =>
                                      i.id === item.id
                                        ? {
                                          ...i,
                                          selected: checked,
                                          rollback_quantity: checked
                                            ? i.quantity
                                            : 0,
                                        }
                                        : i,
                                    ),
                                  );
                                }}
                                className="w-4 h-4 accent-primary"
                              />
                            </TableCell>
                            <TableCell>
                              {renderProductDetails(
                                item,
                                productData || item.product,
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {item.quantity}
                            </TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                min="0"
                                max={item.quantity}
                                value={item.rollback_quantity}
                                onChange={(e) => {
                                  let val = parseInt(e.target.value, 10);
                                  if (isNaN(val) || val < 0) val = 0;
                                  if (val > item.quantity) val = item.quantity;
                                  setRollbackItems((prev) =>
                                    prev.map((i) =>
                                      i.id === item.id
                                        ? {
                                          ...i,
                                          rollback_quantity: val,
                                          selected: val > 0,
                                        }
                                        : i,
                                    ),
                                  );
                                }}
                                className="w-24 ml-auto text-right"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </ShadcnTable>
                </div>
              </CardContent>
            </Card>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseRollbackModal}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitRollback}
                disabled={rollbackSaleMutation.isPending}
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                {rollbackSaleMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Confirm Rollback
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              sale and adjust inventory accordingly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default SalesList;
